import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, FileClock, PlaneTakeoff, CheckCircle2, LogOut, Search, Plus, Trash2,
  Download, Eye, Phone, Mail, MapPin, FileText, X, Loader2, RefreshCw, Building2, MessageSquare,
  Bell, History, KeyRound, UserPlus, ShieldCheck, UserCog, UploadCloud, FileCheck2, Plane, Layers, FileSignature,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr, API } from "@/lib/api";
import { STATUS_FLOW, statusMeta, englishLabel, MONTHS, ENGLISH_LEVELS, COUNTRIES, FLIGHT_OPTIONS, flightLabel, NATIONALITIES } from "@/data/constants";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const StatusBadge = ({ value }) => {
  const m = statusMeta(value);
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
      style={{ backgroundColor: `${m.color}22`, color: m.color, border: `1px solid ${m.color}55` }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: m.color }} /> {m.label}
    </span>
  );
};

const inputCls = "w-full bg-[#0B132B] border border-white/12 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#FFD166]/60";

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("candidates");
  const [stats, setStats] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: "", status: "", english_level: "", passport_status: "", travel_month: "" });
  const [detail, setDetail] = useState(null);
  const [docUrl, setDocUrl] = useState(null);
  const [detailUploading, setDetailUploading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [trips, setTrips] = useState([]);
  const [toDelete, setToDelete] = useState(null);
  const [dupeOpen, setDupeOpen] = useState(false);
  const [dupeKind, setDupeKind] = useState("candidates");
  const [dupeGroups, setDupeGroups] = useState([]);
  const [dupeSel, setDupeSel] = useState({});
  const [dupeLoading, setDupeLoading] = useState(false);
  const [dupeRunning, setDupeRunning] = useState(false);
  const [tripToDelete, setTripToDelete] = useState(null);

  const DEDUPE_EP = {
    candidates: { list: "/candidates/duplicates", run: "/candidates/dedupe" },
    trips: { list: "/trip-confirmations/duplicates", run: "/trip-confirmations/dedupe" },
  };

  useEffect(() => {
    if (ready && !user) nav("/admin/login");
  }, [ready, user, nav]);

  const loadCandidates = useCallback(async () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    const { data } = await api.get("/candidates", { params });
    setCandidates(data);
  }, [filters]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c, e, u, tp] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/contact"),
        api.get("/employers"),
        api.get("/users"),
        api.get("/trip-confirmations"),
      ]);
      setStats(s.data);
      setContacts(c.data);
      setEmployers(e.data);
      setAdmins(u.data);
      setTrips(tp.data);
      await loadCandidates();
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  }, [loadCandidates]);

  useEffect(() => { if (user) loadAll(); }, [user]); // eslint-disable-line
  useEffect(() => { if (user) loadCandidates(); }, [filters]); // eslint-disable-line

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/candidates/${id}`, { status });
      toast.success("Estado actualizado");
      loadCandidates();
      api.get("/admin/stats").then((r) => setStats(r.data));
      if (detail?.id === id) setDetail({ ...detail, status });
    } catch (e) { toast.error(apiErr(e)); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/candidates/${toDelete.id}`);
      toast.success("Candidato eliminado");
      setToDelete(null);
      setDetail(null);
      loadAll();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const viewDoc = async (fileId) => {
    setDocUrl("loading");
    try {
      const res = await api.get(`/files/${fileId}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setDocUrl(null);
    } catch (e) { toast.error(apiErr(e)); setDocUrl(null); }
  };

  const uploadDetailDoc = async (file) => {
    if (!file || !detail) return;
    setDetailUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const res = await api.patch(`/candidates/${detail.id}`, { document_id: data.document_id, document_filename: data.filename });
      setDetail(res.data);
      toast.success("Documento subido");
      loadCandidates();
    } catch (e) { toast.error(apiErr(e)); }
    setDetailUploading(false);
  };

  const openDedupe = async (kind = "candidates") => {
    setDupeKind(kind);
    setDupeLoading(true);
    setDupeOpen(true);
    try {
      const { data } = await api.get(DEDUPE_EP[kind].list);
      setDupeGroups(data.groups);
      const sel = {};
      data.groups.forEach((g) => g.duplicates.forEach((d) => { sel[d.id] = true; }));
      setDupeSel(sel);
    } catch (e) { toast.error(apiErr(e)); setDupeOpen(false); }
    finally { setDupeLoading(false); }
  };

  const runDedupe = async () => {
    const groups = dupeGroups.map((g) => ({
      keep_id: g.keep.id,
      remove_ids: g.duplicates.filter((d) => dupeSel[d.id]).map((d) => d.id),
    })).filter((g) => g.remove_ids.length > 0);
    if (groups.length === 0) { toast.error("Selecciona al menos un duplicado"); return; }
    setDupeRunning(true);
    try {
      const { data } = await api.post(DEDUPE_EP[dupeKind].run, { groups });
      toast.success(`${data.removed} duplicado(s) eliminado(s)`);
      setDupeOpen(false);
      loadAll();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setDupeRunning(false); }
  };

  const doDeleteTrip = async () => {
    try {
      await api.delete(`/trip-confirmations/${tripToDelete.id}`);
      toast.success("Confirmación de viaje eliminada");
      setTripToDelete(null);
      loadAll();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const dupeSelCount = Object.values(dupeSel).filter(Boolean).length;

  const exportCsv = () => {
    const headers = ["Nombre", "Telefono", "Email", "Pais", "Ciudad", "Edad", "Pasaporte", "Mes viaje", "Ingles", "Estado", "Vacante", "Creado"];
    const rows = candidates.map((c) => [
      c.full_name, `${c.country_code || ""} ${c.phone}`, c.email || "", c.country || "", c.state_city || "",
      c.age || "", c.passport_status === "tiene" ? "Sí" : "No", c.travel_month || "", englishLabel(c.english_level),
      statusMeta(c.status).label, c.job_title || "", (c.created_at || "").slice(0, 10),
    ]);
    const csv = [headers, ...rows].map((r) => r.map((f) => `"${String(f).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `candidatos-europejobs-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  if (!ready || !user) {
    return <div className="min-h-screen bg-[#0B132B] flex items-center justify-center"><Loader2 className="animate-spin text-[#FFD166]" /></div>;
  }

  const statCards = stats ? [
    { icon: Users, label: "Total candidatos", value: stats.total, color: "#3B82F6" },
    { icon: FileClock, label: "Por gestionar", value: stats.pending_docs, color: "#F59E0B" },
    { icon: FileClock, label: "Esperando pasaporte", value: stats.waiting_passport, color: "#F59E0B" },
    { icon: PlaneTakeoff, label: "En Polonia / viajando", value: stats.in_poland, color: "#22C55E" },
  ] : [];

  return (
    <div className="min-h-screen bg-[#0B132B]" data-testid="admin-dashboard-container">
      {/* Top bar */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#0B132B]/90 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-xl font-head font-extrabold">europe<span className="text-[#FFD166]">jobs</span></span>
            <span className="hidden sm:inline text-xs text-slate-400 border-l border-white/10 pl-3">Panel Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => { setTab("candidates"); setFilters((f) => ({ ...f, status: "nuevo" })); }}
              data-testid="notif-bell" title="Candidatos nuevos" className="relative text-slate-300 hover:text-[#FFD166]">
              <Bell size={19} />
              {stats?.by_status?.nuevo > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-[#E63946] text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">{stats.by_status.nuevo}</span>
              )}
            </button>
            <span className="hidden sm:inline text-sm text-slate-300">Hola, <strong className="text-white">{user.name}</strong></span>
            <button onClick={loadAll} data-testid="refresh-btn" className="text-slate-300 hover:text-white"><RefreshCw size={18} /></button>
            <button onClick={() => { logout(); nav("/admin/login"); }} data-testid="logout-btn"
              className="flex items-center gap-1.5 text-sm text-slate-300 hover:text-[#E63946]"><LogOut size={16} /> Salir</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {statCards.map((s, i) => (
            <div key={i} className="bg-[#1C2541] rounded-2xl border border-white/10 p-5">
              <s.icon size={22} style={{ color: s.color }} />
              <div className="text-3xl font-head font-extrabold mt-3">{s.value}</div>
              <div className="text-xs text-slate-400 mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { id: "candidates", label: "Candidatos", icon: Users, badge: candidates.length },
            { id: "contacts", label: "Mensajes", icon: MessageSquare, badge: stats?.unread_contacts },
            { id: "employers", label: "Empresas", icon: Building2, badge: stats?.unread_employers },
            { id: "trips", label: "Viajes", icon: Plane, badge: trips.length },
            { id: "agreement", label: "Compromiso", icon: FileSignature },
            { id: "agreements", label: "Acuerdos", icon: FileCheck2 },
            { id: "users", label: "Usuarios", icon: ShieldCheck, badge: admins.length },
          ].map((t) => (
            <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                tab === t.id ? "bg-[#FFD166] text-[#0B132B]" : "bg-[#1C2541] text-slate-300 border border-white/10 hover:border-white/25"
              }`}>
              <t.icon size={16} /> {t.label}
              {t.badge ? <span className={`text-xs px-1.5 rounded-full ${tab === t.id ? "bg-[#0B132B]/20" : "bg-white/10"}`}>{t.badge}</span> : null}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#FFD166]" size={32} /></div>
        ) : tab === "candidates" ? (
          <>
            {/* Filters */}
            <div className="bg-[#1C2541] rounded-2xl border border-white/10 p-4 mb-5 flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[200px]">
                <Search size={16} className="absolute left-3 top-2.5 text-slate-500" />
                <input data-testid="admin-search" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  placeholder="Buscar nombre, teléfono o correo…" className={`${inputCls} pl-9`} />
              </div>
              <select data-testid="filter-status" className={`${inputCls} w-auto`} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                <option value="">Todos los estados</option>
                {STATUS_FLOW.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <select data-testid="filter-passport" className={`${inputCls} w-auto`} value={filters.passport_status} onChange={(e) => setFilters({ ...filters, passport_status: e.target.value })}>
                <option value="">Pasaporte (todos)</option>
                <option value="tiene">Con pasaporte</option>
                <option value="no_tiene">Sin pasaporte</option>
              </select>
              <select data-testid="filter-english" className={`${inputCls} w-auto`} value={filters.english_level} onChange={(e) => setFilters({ ...filters, english_level: e.target.value })}>
                <option value="">Inglés (todos)</option>
                {ENGLISH_LEVELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
              <select data-testid="filter-month" className={`${inputCls} w-auto`} value={filters.travel_month} onChange={(e) => setFilters({ ...filters, travel_month: e.target.value })}>
                <option value="">Mes (todos)</option>
                {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <button onClick={exportCsv} data-testid="export-csv-btn" className="flex items-center gap-1.5 bg-[#253252] text-white text-sm px-3 py-2 rounded-lg hover:bg-[#2f3d5e]"><Download size={15} /> CSV</button>
              <button onClick={() => openDedupe("candidates")} data-testid="dedupe-btn" className="flex items-center gap-1.5 bg-[#253252] text-white text-sm px-3 py-2 rounded-lg hover:bg-[#2f3d5e]"><Layers size={15} /> Quitar duplicados</button>
              <button onClick={() => setAddOpen(true)} data-testid="add-candidate-btn" className="flex items-center gap-1.5 bg-[#FFD166] text-[#0B132B] font-semibold text-sm px-3 py-2 rounded-lg hover:bg-[#E0A926]"><Plus size={15} /> Añadir</button>
            </div>

            {/* Table */}
            <div className="bg-[#1C2541] rounded-2xl border border-white/10 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-400 border-b border-white/10">
                      <th className="px-4 py-3 font-medium">Nombre</th>
                      <th className="px-4 py-3 font-medium">Contacto</th>
                      <th className="px-4 py-3 font-medium">País / Mes</th>
                      <th className="px-4 py-3 font-medium">Pasaporte</th>
                      <th className="px-4 py-3 font-medium">Inglés</th>
                      <th className="px-4 py-3 font-medium">Estado</th>
                      <th className="px-4 py-3 font-medium text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidates.map((c) => (
                      <tr key={c.id} data-testid={`candidate-row-${c.id}`} className="border-b border-white/5 hover:bg-white/5">
                        <td className="px-4 py-3">
                          <div className="font-medium text-white">{c.full_name}</div>
                          <div className="text-xs text-slate-500">{c.job_title}</div>
                        </td>
                        <td className="px-4 py-3 text-slate-300">
                          <div>{c.country_code} {c.phone}</div>
                          <div className="text-xs text-slate-500">{c.email || "—"}</div>
                        </td>
                        <td className="px-4 py-3 text-slate-300">{c.country}<div className="text-xs text-slate-500">{c.travel_month || "—"}</div></td>
                        <td className="px-4 py-3">{c.passport_status === "tiene" ? <span className="text-[#22C55E]">Sí</span> : <span className="text-[#F59E0B]">No</span>}{c.document_id && <FileText size={13} className="inline ml-1 text-slate-400" />}</td>
                        <td className="px-4 py-3 text-slate-300">{englishLabel(c.english_level)}</td>
                        <td className="px-4 py-3">
                          <select value={c.status} data-testid={`status-select-${c.id}`} onChange={(e) => changeStatus(c.id, e.target.value)}
                            className="bg-[#0B132B] border border-white/12 rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none">
                            {STATUS_FLOW.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <button onClick={() => { setDetail(c); setDocUrl(null); }} data-testid={`view-candidate-${c.id}`} className="text-slate-300 hover:text-[#FFD166]"><Eye size={17} /></button>
                            <button onClick={() => setToDelete(c)} data-testid={`delete-candidate-${c.id}`} className="text-slate-300 hover:text-[#E63946]"><Trash2 size={16} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {candidates.length === 0 && <div className="text-center text-slate-400 py-14">No hay candidatos que coincidan con los filtros.</div>}
              </div>
            </div>
          </>
        ) : tab === "contacts" ? (
          <div className="space-y-3">
            {contacts.map((m) => (
              <div key={m.id} className="bg-[#1C2541] rounded-2xl border border-white/10 p-5">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-white">{m.name}</div>
                  <div className="text-xs text-slate-500">{(m.created_at || "").slice(0, 10)}</div>
                </div>
                <div className="flex flex-wrap gap-4 text-sm text-slate-400 mt-1">
                  <span className="flex items-center gap-1"><Mail size={13} /> {m.email}</span>
                  {m.phone && <span className="flex items-center gap-1"><Phone size={13} /> {m.phone}</span>}
                </div>
                <p className="text-slate-300 text-sm mt-3">{m.message}</p>
              </div>
            ))}
            {contacts.length === 0 && <div className="text-center text-slate-400 py-14">Sin mensajes todavía.</div>}
          </div>
        ) : tab === "employers" ? (
          <div className="space-y-3">
            {employers.map((m) => (
              <div key={m.id} className="bg-[#1C2541] rounded-2xl border border-white/10 p-5">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-white flex items-center gap-2"><Building2 size={16} className="text-[#FFD166]" /> {m.company_name}</div>
                  <div className="text-xs text-slate-500">{(m.created_at || "").slice(0, 10)}</div>
                </div>
                <div className="flex flex-wrap gap-4 text-sm text-slate-400 mt-2">
                  <span>{m.contact_name}</span>
                  <span className="flex items-center gap-1"><Mail size={13} /> {m.email}</span>
                  {m.phone && <span className="flex items-center gap-1"><Phone size={13} /> {m.phone}</span>}
                  {m.country && <span className="flex items-center gap-1"><MapPin size={13} /> {m.country}</span>}
                  {m.workers_needed && <span>👷 {m.workers_needed}</span>}
                </div>
                <p className="text-slate-300 text-sm mt-3">{m.message}</p>
              </div>
            ))}
            {employers.length === 0 && <div className="text-center text-slate-400 py-14">Sin solicitudes de empresas todavía.</div>}
          </div>
        ) : tab === "trips" ? (
          <div className="space-y-3" data-testid="trips-panel">
            <div className="flex justify-end">
              <button onClick={() => openDedupe("trips")} data-testid="dedupe-trips-btn" className="flex items-center gap-1.5 bg-[#253252] text-white text-sm px-3 py-2 rounded-lg hover:bg-[#2f3d5e]"><Layers size={15} /> Quitar duplicados</button>
            </div>
            {trips.map((tp) => (
              <div key={tp.id} className="bg-[#1C2541] rounded-2xl border border-white/10 p-5" data-testid={`trip-row-${tp.id}`}>
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-white flex items-center gap-2">
                    <Plane size={16} className="text-[#FFD166]" /> {tp.full_name}
                    {tp.candidate_id
                      ? <span className="text-[10px] bg-[#14B8A6]/20 text-[#5eead4] px-2 py-0.5 rounded-full">Vinculado a candidato</span>
                      : <span className="text-[10px] bg-white/10 text-slate-400 px-2 py-0.5 rounded-full">Sin vincular</span>}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-xs text-slate-500">{(tp.created_at || "").slice(0, 10)}</div>
                    <button onClick={() => setTripToDelete(tp)} data-testid={`delete-trip-${tp.id}`} className="text-slate-300 hover:text-[#E63946]"><Trash2 size={16} /></button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-4 text-sm text-slate-400 mt-2">
                  <span className="flex items-center gap-1"><Phone size={13} /> {tp.country_code} {tp.phone}</span>
                  {tp.email && <span className="flex items-center gap-1"><Mail size={13} /> {tp.email}</span>}
                  {tp.flight_option && <span className="flex items-center gap-1"><Plane size={13} /> {flightLabel(tp.flight_option)}</span>}
                </div>
                <div className="mt-2">
                  {tp.on_next_flight
                    ? <span className="inline-flex items-center gap-1.5 text-xs bg-[#16a34a]/20 text-[#5eead4] px-3 py-1 rounded-full font-semibold">✈️ Sí, quiere viajar el 3 de octubre</span>
                    : <span className="inline-flex items-center gap-1.5 text-xs bg-[#FFD166]/15 text-[#FFD166] px-3 py-1 rounded-full font-semibold"><History size={12} /> Prefiere otro mes: {tp.travel_month || "—"}</span>}
                </div>
                <div className="mt-3 text-sm text-slate-300 flex items-start gap-2">
                  <MapPin size={15} className="text-[#FFD166] shrink-0 mt-0.5" />
                  <span>{[tp.address, tp.colonia, tp.postal_code, tp.city, tp.state].filter(Boolean).join(", ")}{tp.references ? ` · Ref: ${tp.references}` : ""}</span>
                </div>
                {tp.candidate_id && (
                  <button data-testid={`trip-view-candidate-${tp.id}`}
                    onClick={async () => { try { const { data } = await api.get(`/candidates/${tp.candidate_id}`); setDetail(data); } catch (e) { toast.error(apiErr(e)); } }}
                    className="mt-4 inline-flex items-center gap-1.5 text-xs bg-[#FFD166] text-[#0B132B] font-semibold px-4 py-2 rounded-full hover:bg-[#E0A926] transition-colors">
                    <Eye size={13} /> Ver ficha completa
                  </button>
                )}
              </div>
            ))}
            {trips.length === 0 && <div className="text-center text-slate-400 py-14">Sin confirmaciones de viaje todavía.</div>}
          </div>
        ) : tab === "users" ? (
          <UsersPanel admins={admins} currentUser={user} reload={loadAll} />
        ) : tab === "agreement" ? (
          <AgreementPanel candidates={candidates} />
        ) : (
          <AgreementsListPanel />
        )}
      </div>

      {/* Detail modal */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) { setDetail(null); setDocUrl(null); } }}>
        <DialogContent className="bg-[#1C2541] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="candidate-detail-modal">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-head text-2xl flex items-center gap-3">{detail.full_name} <StatusBadge value={detail.status} /></DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4 text-sm mt-2">
                <Field label="Teléfono / WhatsApp" value={`${detail.country_code || ""} ${detail.phone}`} />
                <Field label="Correo" value={detail.email || "—"} />
                <Field label="País" value={detail.country} />
                <Field label="Ciudad / Estado" value={detail.state_city || "—"} />
                <Field label="Edad" value={detail.age || "—"} />
                <Field label="Mes disponible para viajar" value={detail.travel_month || "—"} />
                <Field label="¿Vuelo del 3 de octubre?" value={detail.on_next_flight === true ? "Sí, confirmado" : detail.on_next_flight === false ? "No, prefiere otro mes" : "—"} />
                <Field label="Pasaporte" value={detail.passport_status === "tiene" ? "Tiene pasaporte vigente" : "No tiene (dispuesto a sacarlo)"} />
                <Field label="Nivel de inglés" value={englishLabel(detail.english_level)} />
                <Field label="Vacante" value={detail.job_title || "—"} />
                <Field label="Recibido" value={(detail.created_at || "").slice(0, 16).replace("T", " ")} />
                <div className="col-span-2"><Field label="Domicilio completo" value={detail.home_address || "—"} /></div>
              </div>

              <div className="mt-4">
                <div className="text-xs text-slate-400 mb-1">Vuelo (financiamiento)</div>
                <select data-testid="detail-flight-select" value={detail.flight_option || "por_definir"}
                  onChange={async (e) => { try { await api.patch(`/candidates/${detail.id}`, { flight_option: e.target.value }); setDetail({ ...detail, flight_option: e.target.value }); toast.success("Vuelo actualizado"); loadCandidates(); } catch (err) { toast.error(apiErr(err)); } }}
                  className="bg-[#0B132B] border border-white/12 rounded-lg px-3 py-2 text-sm text-white focus:outline-none w-full max-w-xs">
                  {FLIGHT_OPTIONS.map((fo) => <option key={fo.value} value={fo.value}>{fo.label}</option>)}
                </select>
              </div>

              <div className="mt-4">
                <div className="text-xs text-slate-400 mb-1">Fecha del vuelo</div>
                <input type="date" data-testid="detail-flight-date" value={detail.flight_date || ""}
                  onChange={async (e) => { const v = e.target.value; try { await api.patch(`/candidates/${detail.id}`, { flight_date: v }); setDetail({ ...detail, flight_date: v }); toast.success("Fecha de vuelo actualizada"); loadCandidates(); } catch (err) { toast.error(apiErr(err)); } }}
                  className="bg-[#0B132B] border border-white/12 rounded-lg px-3 py-2 text-sm text-white focus:outline-none w-full max-w-xs [color-scheme:dark]" />
              </div>

              <div className="mt-4">
                <div className="text-xs text-slate-400 mb-1">Cambiar etapa del proceso</div>
                <div className="flex flex-wrap gap-2">
                  {STATUS_FLOW.map((s) => (
                    <button key={s.value} onClick={() => changeStatus(detail.id, s.value)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${detail.status === s.value ? "" : "opacity-60 hover:opacity-100"}`}
                      style={{ backgroundColor: `${s.color}22`, color: s.color, border: `1px solid ${s.color}55` }}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {detail.history?.length > 0 && (
                <div className="mt-5">
                  <div className="text-xs text-slate-400 mb-2 flex items-center gap-1.5"><History size={14} /> Historial del proceso</div>
                  <div className="relative pl-4 border-l border-white/10 space-y-3">
                    {[...detail.history].reverse().map((h, i) => {
                      const m = statusMeta(h.status);
                      return (
                        <div key={i} className="relative" data-testid={`history-item-${i}`}>
                          <span className="absolute -left-[21px] top-1 w-2.5 h-2.5 rounded-full" style={{ backgroundColor: m.color }} />
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold" style={{ color: m.color }}>{m.label}</span>
                            <span className="text-xs text-slate-500">{(h.at || "").slice(0, 16).replace("T", " ")}</span>
                          </div>
                          <div className="text-xs text-slate-400">{h.note}{h.by ? ` · ${h.by}` : ""}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Document */}
              <div className="mt-4">
                <div className="text-xs text-slate-400 mb-2">Documento / Pasaporte</div>
                {detail.document_id ? (
                  <button onClick={() => viewDoc(detail.document_id)} data-testid="view-doc-btn"
                    className="flex items-center gap-2 bg-[#253252] text-white text-sm px-4 py-2 rounded-lg hover:bg-[#2f3d5e]">
                    <FileText size={16} /> {docUrl === "loading" ? "Abriendo…" : `Ver ${detail.document_filename || "documento"}`}
                  </button>
                ) : <div className="text-sm text-slate-500">Sin documento subido.</div>}
                <label data-testid="detail-upload-zone"
                  className="mt-3 flex w-max items-center gap-2 text-xs text-slate-300 border border-dashed border-white/20 rounded-lg px-3 py-2 cursor-pointer hover:border-[#FFD166]/60 transition-colors">
                  <input type="file" accept="image/*,application/pdf,.pdf,.jpg,.jpeg,.png,.webp,.heic" className="hidden" data-testid="detail-file-input"
                    onChange={(e) => uploadDetailDoc(e.target.files?.[0])} />
                  {detailUploading
                    ? <><Loader2 className="animate-spin" size={14} /> Subiendo…</>
                    : <><UploadCloud size={14} className="text-[#FFD166]" /> {detail.document_id ? "Reemplazar documento" : "Subir documento"}</>}
                </label>
              </div>

              <div className="flex flex-wrap gap-3 mt-5 pt-4 border-t border-white/10">
                <a href={`https://wa.me/${(detail.country_code + detail.phone).replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer"
                  className="flex items-center gap-2 bg-[#25D366] text-white text-sm font-semibold px-4 py-2 rounded-lg"><Phone size={15} /> WhatsApp</a>
                {detail.email && <a href={`mailto:${detail.email}`} className="flex items-center gap-2 bg-[#253252] text-white text-sm px-4 py-2 rounded-lg"><Mail size={15} /> Correo</a>}
                <button onClick={() => setToDelete(detail)} className="flex items-center gap-2 text-[#E63946] text-sm px-4 py-2 rounded-lg border border-[#E63946]/40 ml-auto"><Trash2 size={15} /> Eliminar</button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!tripToDelete} onOpenChange={(o) => { if (!o) setTripToDelete(null); }}>
        <AlertDialogContent className="bg-[#1C2541] border-white/10 text-white" data-testid="delete-trip-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar confirmación de viaje?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Se eliminará la confirmación de <strong className="text-white">{tripToDelete?.full_name}</strong>. Esto no elimina al candidato, solo su registro de viaje.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#253252] border-white/10 text-white hover:bg-[#2f3d5e]">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDeleteTrip} data-testid="confirm-delete-trip-btn" className="bg-[#E63946] hover:bg-[#c72f3b]">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={dupeOpen} onOpenChange={(o) => { if (!o) setDupeOpen(false); }}>
        <DialogContent className="bg-[#1C2541] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dedupe-modal">
          <DialogHeader>
            <DialogTitle className="font-head text-xl flex items-center gap-2"><Layers size={20} className="text-[#FFD166]" /> Quitar duplicados</DialogTitle>
          </DialogHeader>
          {dupeLoading ? (
            <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-[#FFD166]" /></div>
          ) : dupeGroups.length === 0 ? (
            <div className="py-14 text-center text-slate-400" data-testid="dedupe-empty">No se encontraron candidatos duplicados. 🎉</div>
          ) : (
            <>
              <p className="text-sm text-slate-400">Encontramos <strong className="text-white">{dupeGroups.length}</strong> grupo(s) con el mismo correo o teléfono. Se conservará el registro más completo y se fusionará su información. Marca los duplicados que quieres eliminar.</p>
              <div className="space-y-4 mt-2">
                {dupeGroups.map((g, gi) => (
                  <div key={g.keep.id} className="border border-white/10 rounded-xl p-4" data-testid={`dedupe-group-${gi}`}>
                    <div className="flex items-start gap-2 bg-[#22C55E]/10 border border-[#22C55E]/30 rounded-lg p-3">
                      <CheckCircle2 size={16} className="text-[#22C55E] mt-0.5 shrink-0" />
                      <div className="text-sm">
                        <div className="font-semibold text-white">{g.keep.full_name} <span className="text-[10px] bg-[#22C55E]/20 text-[#22C55E] px-2 py-0.5 rounded-full ml-1">Se conserva</span></div>
                        <div className="text-xs text-slate-400">{g.keep.country_code} {g.keep.phone} · {g.keep.email || "sin correo"} · {(g.keep.created_at || "").slice(0, 10)}</div>
                      </div>
                    </div>
                    <div className="mt-2 space-y-2">
                      {g.duplicates.map((d) => (
                        <label key={d.id} data-testid={`dedupe-dup-${d.id}`} className="flex items-start gap-2 bg-[#E63946]/5 border border-[#E63946]/20 rounded-lg p-3 cursor-pointer hover:bg-[#E63946]/10">
                          <input type="checkbox" checked={!!dupeSel[d.id]} onChange={() => setDupeSel((p) => ({ ...p, [d.id]: !p[d.id] }))} className="mt-1 accent-[#E63946]" data-testid={`dedupe-check-${d.id}`} />
                          <div className="text-sm">
                            <div className="font-medium text-white">{d.full_name} <span className="text-[10px] bg-[#E63946]/20 text-[#E63946] px-2 py-0.5 rounded-full ml-1">Eliminar</span></div>
                            <div className="text-xs text-slate-400">{d.country_code} {d.phone} · {d.email || "sin correo"} · {(d.created_at || "").slice(0, 10)}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between gap-3 mt-4 pt-4 border-t border-white/10 sticky bottom-0 bg-[#1C2541]">
                <span className="text-sm text-slate-400">{dupeSelCount} seleccionado(s) para eliminar</span>
                <div className="flex gap-2">
                  <button onClick={() => setDupeOpen(false)} className="bg-[#253252] text-white text-sm px-4 py-2 rounded-lg hover:bg-[#2f3d5e]">Cancelar</button>
                  <button onClick={runDedupe} disabled={dupeRunning || dupeSelCount === 0} data-testid="dedupe-confirm-btn" className="flex items-center gap-2 bg-[#E63946] text-white font-semibold text-sm px-4 py-2 rounded-lg hover:bg-[#c72f3b] disabled:opacity-60">{dupeRunning && <Loader2 className="animate-spin" size={15} />} Eliminar {dupeSelCount} duplicado(s)</button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AddCandidateModal open={addOpen} onClose={() => setAddOpen(false)} onDone={() => { setAddOpen(false); loadAll(); }} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent className="bg-[#1C2541] border-white/10 text-white" data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar candidato?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Se eliminará permanentemente a <strong className="text-white">{toDelete?.full_name}</strong>. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#253252] border-white/10 text-white hover:bg-[#2f3d5e]">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-btn" className="bg-[#E63946] hover:bg-[#c72f3b]">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Field = ({ label, value }) => (
  <div><div className="text-xs text-slate-400">{label}</div><div className="text-white">{value}</div></div>
);

function UsersPanel({ admins, currentUser, reload }) {
  const [pwUser, setPwUser] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [toDel, setToDel] = useState(null);
  const [loading, setLoading] = useState(false);

  const savePw = async () => {
    if (newPw.length < 6) { toast.error("La contraseña debe tener al menos 6 caracteres"); return; }
    setLoading(true);
    try {
      await api.patch(`/users/${pwUser.id}`, { password: newPw });
      toast.success("Contraseña actualizada");
      setPwUser(null); setNewPw(""); reload();
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  const addUser = async () => {
    if (!form.name || !form.email || form.password.length < 6) { toast.error("Completa nombre, correo y contraseña (mín. 6)"); return; }
    setLoading(true);
    try {
      await api.post("/users", form);
      toast.success("Administrador añadido");
      setAddOpen(false); setForm({ name: "", email: "", password: "" }); reload();
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/users/${toDel.id}`);
      toast.success("Usuario eliminado");
      setToDel(null); reload();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-5" data-testid="users-panel">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="font-head font-bold text-lg text-white flex items-center gap-2"><ShieldCheck size={18} className="text-[#FFD166]" /> Administradores</h3>
          <p className="text-sm text-slate-400">Cuentas con acceso al panel de administración.</p>
        </div>
        <button onClick={() => setAddOpen(true)} data-testid="add-user-btn" className="flex items-center gap-1.5 bg-[#FFD166] text-[#0B132B] font-semibold text-sm px-3 py-2 rounded-lg hover:bg-[#E0A926]"><UserPlus size={15} /> Añadir admin</button>
      </div>

      <div className="grid gap-3">
        {admins.map((u) => (
          <div key={u.id} data-testid={`user-row-${u.id}`} className="bg-[#1C2541] rounded-2xl border border-white/10 p-5 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-full bg-[#FFD166]/15 flex items-center justify-center text-[#FFD166] font-head font-extrabold">{u.name?.[0]?.toUpperCase()}</div>
              <div>
                <div className="font-semibold text-white flex items-center gap-2">{u.name}{u.id === currentUser.id && <span className="text-[10px] bg-white/10 text-slate-300 px-1.5 py-0.5 rounded-full">Tú</span>}</div>
                <div className="text-sm text-slate-400 flex items-center gap-1.5"><Mail size={13} /> {u.email}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="hidden sm:inline text-xs bg-[#22C55E]/15 text-[#22C55E] border border-[#22C55E]/30 px-2.5 py-1 rounded-full">Administrador</span>
              <button onClick={() => { setPwUser(u); setNewPw(""); }} data-testid={`change-pw-${u.id}`} className="flex items-center gap-1.5 text-slate-300 hover:text-[#FFD166] text-sm border border-white/12 rounded-lg px-3 py-1.5"><KeyRound size={14} /> Contraseña</button>
              {u.id !== currentUser.id && <button onClick={() => setToDel(u)} data-testid={`delete-user-${u.id}`} className="text-slate-300 hover:text-[#E63946]"><Trash2 size={16} /></button>}
            </div>
          </div>
        ))}
      </div>

      <div className="bg-[#0B132B] border border-[#FFD166]/30 rounded-2xl p-5">
        <div className="text-sm font-semibold text-[#FFD166] flex items-center gap-2"><KeyRound size={15} /> Credenciales iniciales</div>
        <p className="text-xs text-slate-400 mt-1">Contraseñas por defecto. Recomendamos cambiarlas con el botón "Contraseña".</p>
        <div className="grid sm:grid-cols-2 gap-3 mt-3 text-sm">
          <div className="bg-[#1C2541] rounded-lg p-3 border border-white/10">
            <div className="text-white font-semibold">Jorge</div>
            <div className="text-slate-400 text-xs mt-1">jorge@europejobs.pro</div>
            <div className="text-slate-300 text-xs mt-1">Contraseña: <span className="font-mono text-[#FFD166]">EuropeJorge2026!</span></div>
          </div>
          <div className="bg-[#1C2541] rounded-lg p-3 border border-white/10">
            <div className="text-white font-semibold">Gracjan</div>
            <div className="text-slate-400 text-xs mt-1">gracjan@europejobs.pro</div>
            <div className="text-slate-300 text-xs mt-1">Contraseña: <span className="font-mono text-[#FFD166]">GracjanEurope2026!</span></div>
          </div>
        </div>
      </div>

      <Dialog open={!!pwUser} onOpenChange={(o) => { if (!o) setPwUser(null); }}>
        <DialogContent className="bg-[#1C2541] border-white/10 text-white max-w-sm" data-testid="change-pw-modal">
          <DialogHeader><DialogTitle className="font-head text-lg">Cambiar contraseña{pwUser ? ` · ${pwUser.name}` : ""}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <input type="password" data-testid="new-pw-input" className={inputCls} placeholder="Nueva contraseña (mín. 6)" value={newPw} onChange={(e) => setNewPw(e.target.value)} />
            <button onClick={savePw} disabled={loading} data-testid="save-pw-btn" className="w-full bg-[#FFD166] text-[#0B132B] font-bold py-3 rounded-full hover:bg-[#E0A926] disabled:opacity-60 flex items-center justify-center gap-2">{loading && <Loader2 className="animate-spin" size={16} />} Guardar</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={(o) => { if (!o) setAddOpen(false); }}>
        <DialogContent className="bg-[#1C2541] border-white/10 text-white max-w-sm" data-testid="add-user-modal">
          <DialogHeader><DialogTitle className="font-head text-lg">Añadir administrador</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <input data-testid="add-user-name" className={inputCls} placeholder="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input data-testid="add-user-email" type="email" className={inputCls} placeholder="Correo" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input data-testid="add-user-password" type="password" className={inputCls} placeholder="Contraseña (mín. 6)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            <button onClick={addUser} disabled={loading} data-testid="add-user-submit" className="w-full bg-[#FFD166] text-[#0B132B] font-bold py-3 rounded-full hover:bg-[#E0A926] disabled:opacity-60 flex items-center justify-center gap-2">{loading && <Loader2 className="animate-spin" size={16} />} Crear administrador</button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDel} onOpenChange={(o) => { if (!o) setToDel(null); }}>
        <AlertDialogContent className="bg-[#1C2541] border-white/10 text-white" data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar administrador?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">Se quitará el acceso de <strong className="text-white">{toDel?.name}</strong> ({toDel?.email}).</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#253252] border-white/10 text-white hover:bg-[#2f3d5e]">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-user-btn" className="bg-[#E63946] hover:bg-[#c72f3b]">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const AGR_DEFAULTS = {
  compromiso: {
    full_name: "", nationality: "Mexicana", passport_number: "", passport_expiry: "", birth_date: "",
    curp: "", rfc: "", address: "", phone: "", email: "",
    receiving_company: "", presentation_place: "",
    travel_date: "3 de octubre de 2026", departure_airport: "Aeropuerto Internacional de Cancún (CUN), México",
    airline: "TUI fly", flight_number: "OR822", departure_time: "22:10 h", currency: "PLN",
    cost_total: "", cost_notes: "",
  },
  clausula: { city: "Ciudad de México", commission_percent: "35", sign_day: "", sign_month: "", year: "2026" },
  promesa: {
    beneficiary_name: "", passport_number: "", nationality: "Mexicana",
    issue_place: "Mlawa, Varsovia", issue_day: "", issue_month: "", issue_year: "2026",
    deadline_day: "", deadline_month: "", deadline_year: "2026",
    position: "Logística, Procesamiento de Alimentos, Empaque de Verduras o Ensamble de Partes Automotrices, según aptitudes y demanda operativa.",
    salary: "Salario por hora de entre 22.00 PLN y 25.00 PLN netos, conforme a la legislación polaca vigente.",
    working_hours: "Turnos de 10 a 12 horas por día, de 5 a 6 días por semana.",
    contract_duration: "Contrato inicial por un (1) año (Umowa na czas określony), con posibilidad de renovación por mutuo acuerdo.",
    housing: "La agencia proporcionará alojamiento; el costo del arrendamiento se deducirá del salario mensual de forma transparente.",
    recruiter_name: "Jenniefer G. Pardo M.",
  },
};

function AgreementPanel({ candidates }) {
  const [d, setD] = useState(AGR_DEFAULTS);
  const [items, setItems] = useState([
    { concept: "Costo total del viaje (incluye seguro de viaje, boleto de avión México–Europa, boleto al destino final y costos del trámite)", amount: "8000" },
  ]);
  const [selectedId, setSelectedId] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [busy, setBusy] = useState("");

  const setF = (sec, k, v) => setD((p) => ({ ...p, [sec]: { ...p[sec], [k]: v } }));

  const prefill = (id) => {
    setSelectedId(id); setLinkUrl("");
    const c = candidates.find((x) => x.id === id);
    if (!c) return;
    const phone = `${c.country_code || ""} ${c.phone || ""}`.trim();
    setD((p) => ({
      ...p,
      compromiso: { ...p.compromiso, full_name: c.full_name || "", phone, email: c.email || "", address: c.home_address || c.state_city || "" },
      clausula: { ...p.clausula },
      promesa: { ...p.promesa, beneficiary_name: c.full_name || "" },
    }));
    toast.success("Datos del candidato cargados");
  };

  const setItem = (i, k, v) => setItems((p) => p.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const total = items.reduce((s, it) => { const n = parseFloat(String(it.amount).replace(/,/g, "").replace(/\s/g, "")); return s + (isNaN(n) ? 0 : n); }, 0);

  const buildPayload = () => {
    const comp = { ...d.compromiso, cost_items: items.filter((it) => it.concept || it.amount) };
    const clausula = { ...d.clausula, full_name: d.compromiso.full_name, passport_number: d.compromiso.passport_number };
    const promesa = { ...d.promesa, beneficiary_name: d.promesa.beneficiary_name || d.compromiso.full_name, passport_number: d.promesa.passport_number || d.compromiso.passport_number, nationality: d.promesa.nationality || d.compromiso.nationality };
    return { compromiso: comp, clausula, promesa };
  };

  const preview = async (key) => {
    setBusy(key);
    try {
      const res = await api.post(`/agreements/preview/${key}`, buildPayload()[key], { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })), "_blank");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const generateLink = async () => {
    if (!d.compromiso.full_name.trim()) { toast.error("Escribe al menos el nombre del candidato"); return; }
    setBusy("link");
    try {
      const { data } = await api.post("/agreements", { candidate_id: selectedId || null, candidate_name: d.compromiso.full_name, ...buildPayload() });
      setLinkUrl(`${window.location.origin}/firmar/${data.token}`);
      toast.success("Link para firmar generado");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const T = ({ sec, k, label, ph, area }) => (
    <div className={area ? "sm:col-span-2" : ""}>
      <div className="text-xs text-slate-400 mb-1">{label}</div>
      {area ? (
        <textarea rows={2} className={inputCls} value={d[sec][k]} onChange={(e) => setF(sec, k, e.target.value)} data-testid={`agr-${sec}-${k}`} />
      ) : (
        <input className={inputCls} placeholder={ph || ""} value={d[sec][k]} onChange={(e) => setF(sec, k, e.target.value)} data-testid={`agr-${sec}-${k}`} />
      )}
    </div>
  );

  const Section = ({ title, children }) => (
    <div className="bg-[#1C2541] rounded-2xl border border-white/10 p-5">
      <div className="text-sm font-semibold text-[#FFD166] mb-3">{title}</div>
      <div className="grid sm:grid-cols-2 gap-3">{children}</div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="agreement-panel">
      <div className="bg-[#0B132B] border border-[#FFD166]/30 rounded-2xl p-5">
        <h3 className="font-head font-bold text-lg text-white flex items-center gap-2"><FileSignature size={18} className="text-[#FFD166]" /> Compromiso + Cláusula + Promesa (Expander Work)</h3>
        <p className="text-sm text-slate-400 mt-1">Rellena los campos editables de los 3 documentos y genera un link para que el candidato los firme. La firma de <strong className="text-white">G. Mitak</strong> ya viene incluida.</p>
        <div className="mt-3">
          <div className="text-xs text-slate-400 mb-1">Cargar un candidato existente (opcional)</div>
          <select className={`${inputCls} max-w-md`} defaultValue="" onChange={(e) => prefill(e.target.value)} data-testid="agr-prefill">
            <option value="">— Seleccionar candidato —</option>
            {candidates.map((c) => <option key={c.id} value={c.id}>{c.full_name} · {c.phone}</option>)}
          </select>
        </div>
      </div>

      <Section title="Documento 1 · Compromiso de viaje">
        <T sec="compromiso" k="full_name" label="Nombre completo *" />
        <T sec="compromiso" k="nationality" label="Nacionalidad" />
        <T sec="compromiso" k="passport_number" label="Pasaporte No." />
        <T sec="compromiso" k="passport_expiry" label="Vigencia" ph="DD/MM/AAAA" />
        <T sec="compromiso" k="birth_date" label="Fecha de nacimiento" ph="DD/MM/AAAA" />
        <T sec="compromiso" k="curp" label="CURP" />
        <T sec="compromiso" k="rfc" label="RFC" />
        <T sec="compromiso" k="phone" label="Teléfono / WhatsApp" />
        <T sec="compromiso" k="address" label="Domicilio" area />
        <T sec="compromiso" k="email" label="Correo" />
        <T sec="compromiso" k="receiving_company" label="Empresa receptora en Polonia" />
        <T sec="compromiso" k="presentation_place" label="Lugar/fecha de presentación en Polonia" />
        <T sec="compromiso" k="airline" label="Aerolínea" />
        <T sec="compromiso" k="flight_number" label="Número de vuelo" />
      </Section>

      <div className="bg-[#1C2541] rounded-2xl border border-white/10 p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold text-[#FFD166]">Costos del viaje</div>
          <select className={`${inputCls} w-auto`} value={d.compromiso.currency} onChange={(e) => setF("compromiso", "currency", e.target.value)} data-testid="agr-currency">
            <option value="MXN">MXN</option><option value="USD">USD</option><option value="EUR">EUR</option><option value="PLN">PLN</option>
          </select>
        </div>
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} className="flex gap-2 items-center">
              <input className={`${inputCls} flex-1`} placeholder="Concepto" value={it.concept} onChange={(e) => setItem(i, "concept", e.target.value)} />
              <input className={`${inputCls} w-32`} placeholder="Monto" value={it.amount} onChange={(e) => setItem(i, "amount", e.target.value)} />
              <button onClick={() => setItems((p) => p.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-[#E63946] shrink-0"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
        <button onClick={() => setItems((p) => [...p, { concept: "", amount: "" }])} className="mt-2 flex items-center gap-1.5 text-sm text-[#FFD166] hover:text-[#E0A926]"><Plus size={15} /> Añadir concepto</button>
        <div className="flex justify-between mt-3 pt-3 border-t border-white/10">
          <span className="text-sm text-slate-400">Total calculado</span>
          <span className="text-lg font-head font-extrabold text-white">{total ? total.toLocaleString("es-MX", { minimumFractionDigits: 2 }) : "0.00"} {d.compromiso.currency}</span>
        </div>
        <button onClick={() => preview("compromiso")} disabled={busy === "compromiso"} className="mt-3 flex items-center gap-2 text-sm bg-[#253252] text-white px-4 py-2 rounded-lg hover:bg-[#2f3d5e]" data-testid="agr-preview-compromiso">{busy === "compromiso" ? <Loader2 className="animate-spin" size={15} /> : <Download size={15} />} Vista previa del Compromiso</button>
      </div>

      <Section title="Documento 2 · Cláusula de responsabilidad económica">
        <T sec="clausula" k="city" label="Ciudad (jurisdicción / firma)" />
        <T sec="clausula" k="commission_percent" label="% de comisión por daños" />
        <T sec="clausula" k="sign_day" label="Día de firma" ph="15" />
        <T sec="clausula" k="sign_month" label="Mes de firma" ph="septiembre" />
        <div className="sm:col-span-2">
          <button onClick={() => preview("clausula")} disabled={busy === "clausula"} className="flex items-center gap-2 text-sm bg-[#253252] text-white px-4 py-2 rounded-lg hover:bg-[#2f3d5e]" data-testid="agr-preview-clausula">{busy === "clausula" ? <Loader2 className="animate-spin" size={15} /> : <Download size={15} />} Vista previa de la Cláusula</button>
        </div>
      </Section>

      <Section title="Documento 3 · Promesa de contratación (Expander Work)">
        <T sec="promesa" k="beneficiary_name" label="Nombre del beneficiario(a)" />
        <T sec="promesa" k="passport_number" label="Número de pasaporte" />
        <T sec="promesa" k="nationality" label="Nacionalidad" />
        <T sec="promesa" k="issue_place" label="Lugar de emisión" />
        <T sec="promesa" k="issue_day" label="Día de emisión" ph="10" />
        <T sec="promesa" k="issue_month" label="Mes de emisión" ph="septiembre" />
        <T sec="promesa" k="deadline_day" label="Día límite de presentación" ph="1" />
        <T sec="promesa" k="deadline_month" label="Mes límite de presentación" ph="octubre" />
        <T sec="promesa" k="position" label="Puesto y funciones" area />
        <T sec="promesa" k="salary" label="Remuneración salarial" area />
        <T sec="promesa" k="working_hours" label="Jornada laboral" area />
        <T sec="promesa" k="contract_duration" label="Duración del contrato" area />
        <T sec="promesa" k="housing" label="Alojamiento" area />
        <T sec="promesa" k="recruiter_name" label="Reclutador (firma agencia)" />
        <div className="sm:col-span-2">
          <button onClick={() => preview("promesa")} disabled={busy === "promesa"} className="flex items-center gap-2 text-sm bg-[#253252] text-white px-4 py-2 rounded-lg hover:bg-[#2f3d5e]" data-testid="agr-preview-promesa">{busy === "promesa" ? <Loader2 className="animate-spin" size={15} /> : <Download size={15} />} Vista previa de la Promesa</button>
        </div>
      </Section>

      <button onClick={generateLink} disabled={busy === "link"} data-testid="agr-genlink-btn"
        className="w-full sm:w-auto flex items-center justify-center gap-2 bg-[#FFD166] text-[#0B132B] font-bold px-6 py-3.5 rounded-full hover:bg-[#E0A926] disabled:opacity-60">
        {busy === "link" ? <Loader2 className="animate-spin" size={18} /> : <FileSignature size={18} />} Generar link para que el candidato firme los 3 documentos
      </button>

      {linkUrl && (
        <div className="bg-[#0B132B] border border-[#FFD166]/40 rounded-2xl p-5" data-testid="agr-link-box">
          <div className="text-sm font-semibold text-[#FFD166] mb-2">Link de firma</div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input readOnly value={linkUrl} className={`${inputCls} flex-1`} onFocus={(e) => e.target.select()} data-testid="agr-link-input" />
            <button onClick={() => { navigator.clipboard.writeText(linkUrl); toast.success("Link copiado"); }} className="bg-[#253252] text-white text-sm px-4 py-2 rounded-lg hover:bg-[#2f3d5e]">Copiar</button>
            <a href={linkUrl} target="_blank" rel="noreferrer" className="bg-[#253252] text-white text-sm px-4 py-2 rounded-lg hover:bg-[#2f3d5e] text-center">Abrir</a>
            <a href={`https://wa.me/?text=${encodeURIComponent(`Hola ${d.compromiso.full_name}, aquí están tus 3 documentos EuropeJobs para revisar y firmar: ${linkUrl}`)}`} target="_blank" rel="noreferrer" className="bg-[#25D366] text-white text-sm font-semibold px-4 py-2 rounded-lg text-center">WhatsApp</a>
          </div>
          <p className="text-xs text-slate-400 mt-2">El candidato abrirá el link, verá los 3 documentos y firmará cada uno en su recuadro.</p>
        </div>
      )}
    </div>
  );
}

function AgreementsListPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [ficha, setFicha] = useState(null);
  const [toDel, setToDel] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [datos, setDatos] = useState(null);
  const [savingDatos, setSavingDatos] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/agreements"); setRows(data); }
    catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openFicha = async (r) => {
    try {
      if (r.candidate_id) {
        const { data } = await api.get(`/candidates/${r.candidate_id}`);
        setFicha({ ...data, _token: r.token });
      } else {
        const { data } = await api.get(`/agreements/${r.token}/full`);
        const f = data.docs?.compromiso?.fields || {};
        setFicha({ full_name: f.full_name, phone: f.phone, email: f.email, home_address: f.address,
          passport_number: f.passport_number, passport_expiry: f.passport_expiry, birth_date: f.birth_date,
          nationality: f.nationality, curp: f.curp, travel_month: f.travel_month, _token: r.token, _noCandidate: true });
      }
    } catch (e) { toast.error(apiErr(e)); }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/agreements/${toDel.token}`, { params: { delete_candidate: !!toDel.candidate_id } });
      toast.success(toDel.candidate_id ? "Candidato y acuerdo eliminados" : "Acuerdo eliminado");
      setToDel(null); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setDeleting(false); }
  };

  const viewDoc = async (fileId) => {
    try {
      const res = await api.get(`/files/${fileId}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const openDatos = async (token) => {
    try {
      const { data } = await api.get(`/agreements/${token}/full`);
      const f = data.docs?.compromiso?.fields || {};
      setDatos({ token, full_name: f.full_name || "", nationality: f.nationality || "Mexicana",
        passport_number: f.passport_number || "", passport_expiry: f.passport_expiry || "",
        birth_date: f.birth_date || "", curp: f.curp || "", phone: f.phone || "", email: f.email || "", address: f.address || "" });
    } catch (e) { toast.error(apiErr(e)); }
  };
  const saveDatos = async () => {
    setSavingDatos(true);
    try {
      const { token, ...fields } = datos;
      await api.patch(`/agreements/${token}/candidate`, fields);
      toast.success("Datos del candidato actualizados en los 3 documentos");
      setDatos(null); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSavingDatos(false); }
  };

  const openEdit = async (token) => {
    try { const { data } = await api.get(`/agreements/${token}/full`); setEdit({ token, fields: data.docs.compromiso.fields || {} }); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/agreements/${edit.token}/doc/compromiso`, { fields: edit.fields });
      toast.success("Datos del vuelo actualizados");
      setEdit(null); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const FIELDS = [
    ["travel_date", "Fecha del viaje"], ["departure_airport", "Aeropuerto de salida"],
    ["airline", "Aerolínea"], ["flight_number", "Número de vuelo"], ["departure_time", "Hora de salida"],
    ["checkin_time", "Hora límite de presentación"], ["first_destination", "Primer destino"],
    ["final_destination", "Destino final"], ["receiving_company", "Empresa receptora en Polonia"],
    ["presentation_place", "Lugar/fecha de presentación"],
  ];

  return (
    <div className="space-y-3" data-testid="agreements-list-panel">
      {loading ? (
        <div className="py-14 flex justify-center"><Loader2 className="animate-spin text-[#FFD166]" /></div>
      ) : rows.length === 0 ? (
        <div className="text-center text-slate-400 py-14">Aún no hay acuerdos. Se crean automáticamente cuando un candidato completa la Etapa 2, o manualmente desde la pestaña "Compromiso".</div>
      ) : rows.map((r) => (
        <div key={r.token} className="bg-[#1C2541] rounded-2xl border border-white/10 p-5" data-testid={`agr-row-${r.token}`}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="font-semibold text-white">{r.candidate_name || "—"}</div>
              <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-1.5">
                <CheckCircle2 size={12} className="text-[#14B8A6]" /> Confirmó viaje: {(r.created_at || "").slice(0, 10) || "—"}
              </div>
              <div className="mt-1.5">
                {r.all_signed && r.sign_method === "web" ? (
                  <span data-testid={`agr-status-${r.token}`} className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#16a34a]/20 text-[#5eead4] px-2.5 py-1 rounded-full">
                    <FileSignature size={12} /> Firmó los 3 por la web · {(r.completed_at || "").slice(0, 10)}
                  </span>
                ) : r.sign_method === "whatsapp" ? (
                  <span data-testid={`agr-status-${r.token}`} className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#25D366]/20 text-[#25D366] px-2.5 py-1 rounded-full">
                    <MessageSquare size={12} /> Eligió firmar y enviar por WhatsApp
                  </span>
                ) : (
                  <span data-testid={`agr-status-${r.token}`} className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white/10 text-slate-400 px-2.5 py-1 rounded-full">
                    <FileClock size={12} /> Pendiente de firma
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {["compromiso", "clausula", "promesa"].map((k) => (
                <span key={k} className={`text-[10px] px-2 py-0.5 rounded-full ${r.signed?.[k] ? "bg-[#16a34a]/20 text-[#5eead4]" : "bg-white/10 text-slate-400"}`}>{k} {r.signed?.[k] ? "✓" : "•"}</span>
              ))}
              <a href={`${window.location.origin}/firmar/${r.token}`} target="_blank" rel="noreferrer" className="text-sm bg-[#253252] text-white px-3 py-1.5 rounded-lg hover:bg-[#2f3d5e]">Abrir link</a>
              <button onClick={() => openFicha(r)} data-testid={`ficha-agr-${r.token}`} className="flex items-center gap-1.5 text-sm bg-[#253252] text-white px-3 py-1.5 rounded-lg hover:bg-[#2f3d5e]"><Eye size={14} /> Ver ficha</button>
              <button onClick={() => openDatos(r.token)} data-testid={`datos-agr-${r.token}`} className="flex items-center gap-1.5 text-sm bg-[#253252] text-white px-3 py-1.5 rounded-lg hover:bg-[#2f3d5e]"><UserCog size={14} /> Editar datos</button>
              <button onClick={() => openEdit(r.token)} data-testid={`edit-agr-${r.token}`} className="text-sm bg-[#FFD166] text-[#0B132B] font-semibold px-3 py-1.5 rounded-lg hover:bg-[#E0A926]">Editar vuelo</button>
              <button onClick={() => setToDel(r)} data-testid={`del-agr-${r.token}`} className="flex items-center gap-1.5 text-sm text-[#E63946] px-3 py-1.5 rounded-lg border border-[#E63946]/40 hover:bg-[#E63946]/10"><Trash2 size={14} /> Borrar</button>
            </div>
          </div>
          <div className="mt-4 pt-3 border-t border-white/10" data-testid={`agr-downloads-${r.token}`}>
            <div className="text-xs text-slate-400 mb-2 flex items-center gap-1.5">
              <FileText size={13} className="text-[#FFD166]" />
              {r.all_signed && r.sign_method === "web" ? "Documentos firmados (candidato + empresa):" : "Ver documentos (borrador / estado actual):"}
            </div>
            <div className="flex flex-wrap gap-2">
              {["compromiso", "clausula", "promesa"].map((k) => (
                <a key={k} href={`${API}/agreements/${r.token}/pdf/${k}?ts=${r.signed_at?.[k] ? new Date(r.signed_at[k]).getTime() : Date.now()}`}
                  target="_blank" rel="noreferrer" data-testid={`agr-dl-${r.token}-${k}`}
                  className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg ${r.signed?.[k] ? "bg-[#16a34a]/20 text-[#5eead4] hover:bg-[#16a34a]/30" : "bg-[#253252] text-white hover:bg-[#2f3d5e]"}`}>
                  {r.signed?.[k] ? <CheckCircle2 size={13} /> : <FileText size={13} />} {k} <Download size={13} className="text-[#FFD166]" />
                </a>
              ))}
            </div>
          </div>
        </div>
      ))}

      <Dialog open={!!edit} onOpenChange={(o) => { if (!o) setEdit(null); }}>
        <DialogContent className="bg-[#1C2541] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-agreement-modal">
          <DialogHeader><DialogTitle className="font-head text-lg flex items-center gap-2"><Plane size={18} className="text-[#FFD166]" /> Editar datos del vuelo</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid sm:grid-cols-2 gap-3">
              {FIELDS.map(([k, label]) => (
                <div key={k}>
                  <div className="text-xs text-slate-400 mb-1">{label}</div>
                  <input className={inputCls} value={edit.fields[k] || ""} onChange={(e) => setEdit((p) => ({ ...p, fields: { ...p.fields, [k]: e.target.value } }))} data-testid={`edit-field-${k}`} />
                </div>
              ))}
              <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
                <button onClick={() => setEdit(null)} className="bg-[#253252] text-white text-sm px-4 py-2 rounded-lg hover:bg-[#2f3d5e]">Cancelar</button>
                <button onClick={save} disabled={saving} data-testid="save-agr-flight" className="flex items-center gap-2 bg-[#FFD166] text-[#0B132B] font-semibold text-sm px-4 py-2 rounded-lg disabled:opacity-60">{saving && <Loader2 className="animate-spin" size={15} />} Guardar</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!datos} onOpenChange={(o) => { if (!o) setDatos(null); }}>
        <DialogContent className="bg-[#1C2541] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-datos-modal">
          <DialogHeader><DialogTitle className="font-head text-lg flex items-center gap-2"><UserCog size={18} className="text-[#FFD166]" /> Editar datos del candidato</DialogTitle></DialogHeader>
          {datos && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <div className="text-xs text-slate-400 mb-1">Nombre completo</div>
                <input className={inputCls} value={datos.full_name} onChange={(e) => setDatos((p) => ({ ...p, full_name: e.target.value }))} data-testid="datos-full_name" />
              </div>
              <div>
                <div className="text-xs text-slate-400 mb-1">Nacionalidad</div>
                <select className={inputCls} value={NATIONALITIES.includes(datos.nationality) ? datos.nationality : "Otra"}
                  onChange={(e) => setDatos((p) => ({ ...p, nationality: e.target.value === "Otra" ? "" : e.target.value }))} data-testid="datos-nationality">
                  {NATIONALITIES.map((n) => <option key={n} value={n}>{n === "Otra" ? "Otra…" : n}</option>)}
                </select>
                {!NATIONALITIES.includes(datos.nationality) && (
                  <input className={`${inputCls} mt-2`} value={datos.nationality} onChange={(e) => setDatos((p) => ({ ...p, nationality: e.target.value }))} placeholder="Escribe la nacionalidad" data-testid="datos-nationality-other" />
                )}
              </div>
              <div>
                <div className="text-xs text-slate-400 mb-1">CURP</div>
                <input className={inputCls} value={datos.curp} onChange={(e) => setDatos((p) => ({ ...p, curp: e.target.value.toUpperCase() }))} data-testid="datos-curp" />
              </div>
              <div>
                <div className="text-xs text-slate-400 mb-1">Pasaporte No.</div>
                <input className={inputCls} value={datos.passport_number} onChange={(e) => setDatos((p) => ({ ...p, passport_number: e.target.value }))} data-testid="datos-passport" />
              </div>
              <div>
                <div className="text-xs text-slate-400 mb-1">Vigencia del pasaporte</div>
                <input className={inputCls} value={datos.passport_expiry} onChange={(e) => setDatos((p) => ({ ...p, passport_expiry: e.target.value }))} placeholder="DD/MM/AAAA" data-testid="datos-passport_expiry" />
              </div>
              <div>
                <div className="text-xs text-slate-400 mb-1">Fecha de nacimiento</div>
                <input className={inputCls} value={datos.birth_date} onChange={(e) => setDatos((p) => ({ ...p, birth_date: e.target.value }))} placeholder="DD/MM/AAAA" data-testid="datos-birth_date" />
              </div>
              <div>
                <div className="text-xs text-slate-400 mb-1">Teléfono / WhatsApp</div>
                <input className={inputCls} value={datos.phone} onChange={(e) => setDatos((p) => ({ ...p, phone: e.target.value }))} data-testid="datos-phone" />
              </div>
              <div>
                <div className="text-xs text-slate-400 mb-1">Correo</div>
                <input className={inputCls} value={datos.email} onChange={(e) => setDatos((p) => ({ ...p, email: e.target.value }))} data-testid="datos-email" />
              </div>
              <div className="sm:col-span-2">
                <div className="text-xs text-slate-400 mb-1">Domicilio (en el compromiso)</div>
                <input className={inputCls} value={datos.address} onChange={(e) => setDatos((p) => ({ ...p, address: e.target.value }))} placeholder="Dejar en blanco para que el candidato lo rellene" data-testid="datos-address" />
              </div>
              <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
                <button onClick={() => setDatos(null)} className="bg-[#253252] text-white text-sm px-4 py-2 rounded-lg hover:bg-[#2f3d5e]">Cancelar</button>
                <button onClick={saveDatos} disabled={savingDatos} data-testid="save-datos-btn" className="flex items-center gap-2 bg-[#FFD166] text-[#0B132B] font-semibold text-sm px-4 py-2 rounded-lg disabled:opacity-60">{savingDatos && <Loader2 className="animate-spin" size={15} />} Guardar</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!ficha} onOpenChange={(o) => { if (!o) setFicha(null); }}>
        <DialogContent className="bg-[#1C2541] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ficha-modal">
          {ficha && (
            <>
              <DialogHeader><DialogTitle className="font-head text-xl flex items-center gap-2"><Eye size={18} className="text-[#FFD166]" /> Ficha completa · {ficha.full_name || "—"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4 text-sm mt-2">
                <Field label="Teléfono / WhatsApp" value={`${ficha.country_code || ""} ${ficha.phone || ""}`.trim() || "—"} />
                <Field label="Correo" value={ficha.email || "—"} />
                <Field label="Nacionalidad" value={ficha.nationality || "—"} />
                <Field label="CURP" value={ficha.curp || "—"} />
                <Field label="Pasaporte" value={ficha.passport_number || "—"} />
                <Field label="Vigencia pasaporte" value={ficha.passport_expiry || "—"} />
                <Field label="Fecha de nacimiento" value={ficha.birth_date || "—"} />
                <Field label="Mes de viaje" value={ficha.travel_month || "—"} />
                <Field label="Ciudad / Estado" value={ficha.state_city || "—"} />
                <Field label="Vuelo (financiamiento)" value={flightLabel(ficha.flight_option)} />
                <div className="col-span-2"><Field label="Domicilio completo" value={ficha.home_address || "—"} /></div>
              </div>
              {ficha._noCandidate && <p className="text-xs text-slate-500 mt-3">Este acuerdo no está vinculado a un candidato registrado; se muestran los datos del compromiso.</p>}
              <div className="mt-4">
                <div className="text-xs text-slate-400 mb-2">Pasaporte / Documento subido</div>
                {ficha.document_id ? (
                  <button onClick={() => viewDoc(ficha.document_id)} data-testid="ficha-view-doc"
                    className="flex items-center gap-2 bg-[#253252] text-white text-sm px-4 py-2 rounded-lg hover:bg-[#2f3d5e]">
                    <FileText size={16} className="text-[#FFD166]" /> Ver {ficha.document_filename || "documento del pasaporte"}
                  </button>
                ) : <div className="text-sm text-slate-500">Sin documento de pasaporte subido.</div>}
              </div>
              {ficha.history?.length > 0 && (
                <div className="mt-5">
                  <div className="text-xs text-slate-400 mb-2 flex items-center gap-1.5"><History size={14} /> Historial del proceso</div>
                  <div className="relative pl-4 border-l border-white/10 space-y-3">
                    {[...ficha.history].reverse().map((h, i) => {
                      const m = statusMeta(h.status);
                      return (
                        <div key={i} className="relative">
                          <span className="absolute -left-[21px] top-1 w-2.5 h-2.5 rounded-full" style={{ backgroundColor: m.color }} />
                          <div className="flex items-center gap-2 flex-wrap"><span className="text-sm font-semibold" style={{ color: m.color }}>{m.label}</span><span className="text-xs text-slate-500">{(h.at || "").slice(0, 16).replace("T", " ")}</span></div>
                          <div className="text-xs text-slate-400">{h.note}{h.by ? ` · ${h.by}` : ""}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-white/10">
                {["compromiso", "clausula", "promesa"].map((k) => (
                  <a key={k} href={`${API}/agreements/${ficha._token}/pdf/${k}?ts=${Date.now()}`} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#253252] text-white px-3 py-1.5 rounded-lg hover:bg-[#2f3d5e]"><FileText size={13} /> {k}</a>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDel} onOpenChange={(o) => { if (!o) setToDel(null); }}>
        <AlertDialogContent className="bg-[#1C2541] border-white/10 text-white" data-testid="delete-agreement-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Borrar acuerdo{toDel?.candidate_id ? " y candidato" : ""}?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Se eliminará el acuerdo de <strong className="text-white">{toDel?.candidate_name || "—"}</strong>{toDel?.candidate_id ? " junto con su ficha de candidato y su confirmación de viaje" : ""}. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#253252] border-white/10 text-white hover:bg-[#2f3d5e]">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} disabled={deleting} data-testid="confirm-delete-agr-btn" className="bg-[#E63946] hover:bg-[#c72f3b]">Borrar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AddCandidateModal({ open, onClose, onDone }) {
  const empty = { full_name: "", country_code: "+52", phone: "", email: "", country: "México", state_city: "", age: "", passport_status: "tiene", travel_month: "", english_level: "ninguno", flight_option: "por_definir", status: "nuevo", notes: "", job_title: "SHEIN Polonia - Logística" };
  const [f, setF] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [doc, setDoc] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDoc(data);
      toast.success("Documento subido");
    } catch (e) { toast.error(apiErr(e)); } finally { setUploading(false); }
  };
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.full_name || !f.phone || !f.travel_month) { toast.error("Nombre, teléfono y mes son obligatorios."); return; }
    setLoading(true);
    try {
      await api.post("/candidates/admin", { ...f, age: f.age ? parseInt(f.age, 10) : null, document_id: doc?.document_id || null, document_filename: doc?.filename || null });
      toast.success("Candidato añadido");
      setF(empty);
      setDoc(null);
      onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#1C2541] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-candidate-modal">
        <DialogHeader><DialogTitle className="font-head text-xl">Añadir candidato</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <input data-testid="add-name" className={inputCls} placeholder="Nombre completo *" value={f.full_name} onChange={(e) => set("full_name", e.target.value)} />
          <div className="grid grid-cols-3 gap-2">
            <select className={inputCls} value={f.country_code} onChange={(e) => set("country_code", e.target.value)}>
              {COUNTRIES.map((c) => <option key={c.code + c.name} value={c.code}>{c.code}</option>)}
            </select>
            <input data-testid="add-phone" className={`${inputCls} col-span-2`} placeholder="Teléfono *" value={f.phone} onChange={(e) => set("phone", e.target.value)} />
          </div>
          <input className={inputCls} placeholder="Correo" value={f.email} onChange={(e) => set("email", e.target.value)} />
          <div className="grid grid-cols-2 gap-2">
            <select className={inputCls} value={f.country} onChange={(e) => set("country", e.target.value)}>
              {COUNTRIES.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
            </select>
            <input className={inputCls} placeholder="Ciudad / Estado" value={f.state_city} onChange={(e) => set("state_city", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input type="number" className={inputCls} placeholder="Edad" value={f.age} onChange={(e) => set("age", e.target.value)} />
            <select className={inputCls} value={f.travel_month} onChange={(e) => set("travel_month", e.target.value)}>
              <option value="">Mes de viaje *</option>
              {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select className={inputCls} value={f.passport_status} onChange={(e) => set("passport_status", e.target.value)}>
              <option value="tiene">Con pasaporte</option>
              <option value="no_tiene">Sin pasaporte</option>
            </select>
            <select className={inputCls} value={f.english_level} onChange={(e) => set("english_level", e.target.value)}>
              {ENGLISH_LEVELS.map((l) => <option key={l.value} value={l.value}>Inglés: {l.label}</option>)}
            </select>
          </div>
          <select className={inputCls} value={f.status} onChange={(e) => set("status", e.target.value)}>
            {STATUS_FLOW.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <div>
            <div className="text-xs text-slate-400 mb-1 flex items-center gap-1.5"><Plane size={13} /> Vuelo (financiamiento)</div>
            <select data-testid="add-flight" className={inputCls} value={f.flight_option} onChange={(e) => set("flight_option", e.target.value)}>
              {FLIGHT_OPTIONS.map((fo) => <option key={fo.value} value={fo.value}>{fo.label}</option>)}
            </select>
          </div>
          <div>
            <div className="text-xs text-slate-400 mb-1">Documentos / Pasaporte (foto o PDF)</div>
            <label data-testid="add-upload-zone"
              className="border-2 border-dashed border-white/15 rounded-xl p-4 text-center cursor-pointer hover:border-[#FFD166]/60 transition-colors flex items-center justify-center gap-2 min-h-[56px]">
              <input type="file" accept="image/*,application/pdf,.pdf,.jpg,.jpeg,.png,.webp,.heic" className="hidden" data-testid="add-file-input"
                onChange={(e) => handleFile(e.target.files?.[0])} />
              {uploading ? (
                <span className="flex items-center gap-2 text-slate-300 text-sm"><Loader2 className="animate-spin" size={16} /> Subiendo…</span>
              ) : doc ? (
                <span className="flex items-center gap-2 text-[#22C55E] text-sm"><FileCheck2 size={16} /> {doc.filename} · toca para cambiar</span>
              ) : (
                <span className="flex items-center gap-2 text-slate-400 text-sm"><UploadCloud size={16} className="text-[#FFD166]" /> Subir documento (opcional)</span>
              )}
            </label>
          </div>
          <textarea className={inputCls} rows={2} placeholder="Notas internas" value={f.notes} onChange={(e) => set("notes", e.target.value)} />
          <button onClick={submit} disabled={loading || uploading} data-testid="add-submit"
            className="w-full bg-[#FFD166] text-[#0B132B] font-bold py-3 rounded-full hover:bg-[#E0A926] disabled:opacity-60 flex items-center justify-center gap-2">
            {(loading || uploading) && <Loader2 className="animate-spin" size={16} />} {uploading ? "Subiendo documento…" : "Guardar candidato"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
